import { apiUpdateTrafficMonitoring, apiCreateTrafficMonitoring } from "apis/TrafficMonitoringApi";
import { uploadFileToS3 } from "utils/function";
import { MSG_CAMERA_EMPTY, MSG_DAYTIME_CAUTION_EMPTY, MSG_DAYTIME_CAUTION_WARNING_INVALID, MSG_DAYTIME_WARNING_EMPTY, MSG_DIRECTION_EMPTY, MSG_FILE_EMPTY, MSG_NIGHTTIME_CAUTION_EMPTY, MSG_NIGHTTIME_CAUTION_WARNING_INVALID, MSG_NIGHTTIME_WARNING_EMPTY, MSG_ROUTE_ID_EMPTY, MSG_ROUTE_NAME_EMPTY, MSG_SCENARIO_EMPTY } from "./constants";

export const retrieveNewFileName = (file) => {
    if (!file || !file.name) return null;
    const nameParts = file.name.split('.');
    const extension = nameParts.pop();
    const now = new Date();
    const timestamp = now.getFullYear().toString() +
        (now.getMonth() + 1).toString().padStart(2, '0') +
        now.getDate().toString().padStart(2, '0') +
        now.getHours().toString().padStart(2, '0') +
        now.getMinutes().toString().padStart(2, '0') +
        now.getSeconds().toString().padStart(2, '0');
    const originalName = nameParts.join('.');
    const newFileName = `${originalName}_${timestamp}.${extension}`;
    return newFileName;
}

export const handleUpdateTrafficMonitoring = async (id, data, uploadedFile) => {
    try {
        // Upload route data file if a new file is selected
        if (uploadedFile) {
            const res = await uploadFileToS3(uploadedFile, `traffic_monitoring_route_data/${retrieveNewFileName(uploadedFile)}`);
            data.routeDataKey = res;
        }
        // Update existing traffic monitoring route
        const result = await apiUpdateTrafficMonitoring(id, {
            ...data,
            type: 'TrafficMonitoring',
            trafficDirections: JSON.stringify(data.trafficDirections)
        });
        return result;
    } catch (error) {
        throw error;
    }
};

export const handleCreateTrafficMonitoring = async (data, uploadedFile) => {
    try {
        // Upload route data file if a new file is selected
        if (uploadedFile) {
            const res = await uploadFileToS3(uploadedFile, `traffic_monitoring_route_data/${retrieveNewFileName(uploadedFile)}`);
            data.routeDataKey = res;
        }
        // Create new traffic monitoring route
        const result = await apiCreateTrafficMonitoring({
            ...data,
            type: 'TrafficMonitoring',
            trafficDirections: JSON.stringify(data.trafficDirections)
        });
        return result;
    } catch (error) {
        throw error;
    }
}

export const checkFileFormat = async (file) => {
    try {
        const fileContent = await file.text();
        const geoJsonData = JSON.parse(fileContent);
        
        // Validate basic GeoJSON structure
        if (!geoJsonData.type || geoJsonData.type !== 'FeatureCollection') return false;
        if (!geoJsonData.features || !Array.isArray(geoJsonData.features)) return false;
        if (geoJsonData.features.length === 0 || geoJsonData.features.length > 1) return false;
        if (!Array.isArray(geoJsonData.features[0].geometry.coordinates)) return false;

        return true;
    } catch (error) {
        throw error;
    }
}

export const checkAndRetrieveInfoFromForm = (event, routeDataKey, trafficDirections) => {
    const formData = new FormData(event.target);
    const routeId = formData.get('routeId');
    const routeName = formData.get('routeName');
    const data = {
        routeId: routeId,
        routeName: routeName,
        routeDataKey: routeDataKey,
        trafficDirections: []
    };
    if (routeId.trim() === '') return {data, error: MSG_ROUTE_ID_EMPTY };
    if (routeName.trim() === '')  return {data, error: MSG_ROUTE_NAME_EMPTY};
    if (!routeDataKey || routeDataKey.trim() === '') return {data, error: MSG_FILE_EMPTY};

    // Collect direction data
    for (let directionIndex = 0; directionIndex < trafficDirections.length; directionIndex++) {
        const directionName = formData.get(`directionName_${directionIndex}`);
        // const cameraSettings = trafficDirections[directionIndex].cameraSettings;
        if (!directionName || directionName.trim() === '')
            return {data, error: MSG_DIRECTION_EMPTY(directionIndex + 1)};
        const direction = { directionName, cameraSettings: [] };
        // Determine number of camera settings for this direction
        let cameraSettingsCount = 1;
        if (formData.get(`no_${directionIndex}_${1}`))
            cameraSettingsCount = 2;
        // Collect camera settings for each direction
        for (let order = 0; order < cameraSettingsCount; order++) {
            const cameraId = formData.get(`cameraId_${directionIndex}_${order}`);
            const scenarioId = formData.get(`scenarioId_${directionIndex}_${order}`);
            const scenarioLabel = formData.get(`scenarioLabel_${directionIndex}_${order}`);
            const daytimeWarning = formData.get(`daytimeWarning_${directionIndex}_${order}`);
            const daytimeCaution = formData.get(`daytimeCaution_${directionIndex}_${order}`);
            const nighttimeWarning = formData.get(`nighttimeWarning_${directionIndex}_${order}`);
            const nighttimeCaution = formData.get(`nighttimeCaution_${directionIndex}_${order}`);

            if (!cameraId || cameraId.trim() === '' )
                return {data, error: MSG_CAMERA_EMPTY(order + 1)};
            if (!scenarioId || scenarioId.trim() === '' )
                return {data, error: MSG_SCENARIO_EMPTY(order + 1)};
            if (!daytimeWarning || daytimeWarning.trim() === '' )
                return {data, error: MSG_DAYTIME_WARNING_EMPTY(order + 1)};
            if (!daytimeCaution || daytimeCaution.trim() === '' )
                return {data, error: MSG_DAYTIME_CAUTION_EMPTY(order + 1)};
            if (Number(daytimeWarning) > Number(daytimeCaution))
                return {data, error: MSG_DAYTIME_CAUTION_WARNING_INVALID};
            if (!nighttimeWarning || nighttimeWarning.trim() === '' )
                return {data, error: MSG_NIGHTTIME_WARNING_EMPTY(order + 1)};
            if (!nighttimeCaution || nighttimeCaution.trim() === '' )
                return {data, error: MSG_NIGHTTIME_CAUTION_EMPTY(order + 1)};
            if (Number(nighttimeWarning) > Number(nighttimeCaution))
                return {data, error: MSG_NIGHTTIME_CAUTION_WARNING_INVALID};


            if (scenarioId || cameraId) {
                direction.cameraSettings.push({
                    cameraId,
                    scenarioId,
                    scenarioLabel,
                    daytimeWarning,
                    daytimeCaution,
                    nighttimeWarning,
                    nighttimeCaution
                });
            }
        }

        if (directionName || direction.cameraSettings.length > 0) {
            data.trafficDirections.push(direction);
        }
    }

    return {data, error: null};
}

export const CAMERA_SETTING_INITIAL = {
    cameraId: '',
    scenarioId: '',
    scenarioLabel: '',
    daytimeCaution: '10',
    daytimeWarning: '5',
    nighttimeCaution: '1',
    nighttimeWarning: '0',
};